import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { axiosInstance } from "../utils/axiosConfig";
import "../styles/CoverLetterBuilder.css";

interface CoverLetterData {
  id?: string;
  title: string;
  template: "professional" | "modern" | "simple";
  content: {
    recipientName: string;
    recipientTitle: string;
    companyName: string;
    companyAddress: string;
    jobTitle: string;
    salutation: string;
    openingParagraph: string;
    bodyParagraph1: string;
    bodyParagraph2: string;
    closingParagraph: string;
    signOff: string;
    senderName: string;
    senderEmail: string;
    senderPhone: string;
    senderAddress: string;
    date: string;
  };
}

const EMPTY_COVER_LETTER: CoverLetterData = {
  title: "My Cover Letter",
  template: "professional",
  content: {
    recipientName: "",
    recipientTitle: "",
    companyName: "",
    companyAddress: "",
    jobTitle: "",
    salutation: "Dear Hiring Manager,",
    openingParagraph: "",
    bodyParagraph1: "",
    bodyParagraph2: "",
    closingParagraph: "",
    signOff: "Sincerely,",
    senderName: "",
    senderEmail: "",
    senderPhone: "",
    senderAddress: "",
    date: new Date().toISOString().split("T")[0],
  },
};

type Section = "recipient" | "intro" | "body" | "closing" | "sender";

const SECTIONS: { key: Section; label: string; icon: string }[] = [
  { key: "recipient", label: "Recipient Info", icon: "👤" },
  { key: "intro", label: "Introduction", icon: "👋" },
  { key: "body", label: "Body", icon: "📝" },
  { key: "closing", label: "Closing", icon: "✅" },
  { key: "sender", label: "Your Info", icon: "📧" },
];

const TEMPLATES = [
  { value: "professional", label: "Professional", description: "Traditional, formal layout" },
  { value: "modern", label: "Modern", description: "Clean, contemporary design" },
  { value: "simple", label: "Simple", description: "Minimalist, straightforward" },
];

const CoverLetterBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { letterId } = useParams<{ letterId?: string }>();
  const [searchParams] = useSearchParams();
  
  const [letterData, setLetterData] = useState<CoverLetterData>(EMPTY_COVER_LETTER);
  const [activeSection, setActiveSection] = useState<Section>("recipient");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [showTailorModal, setShowTailorModal] = useState(false);
  const [tailorJobDesc, setTailorJobDesc] = useState("");
  const [tailoring, setTailoring] = useState(false);

  // Load user profile for auto-fill
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await axiosInstance.get("/auth/profile");
        setUserProfile(response.data);
        
        // Auto-fill sender info if creating new letter
        if (!letterId && response.data) {
          setLetterData(prev => ({
            ...prev,
            content: {
              ...prev.content,
              senderName: response.data.fullName || "",
              senderEmail: response.data.email || "",
              senderPhone: response.data.phone || "",
            }
          }));
        }
      } catch (error) {
        console.error("Failed to load profile:", error);
      }
    };
    loadProfile();
  }, [letterId]);

  // Load existing cover letter if editing
  useEffect(() => {
    const loadLetter = async (id: string) => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(`/auth/cover-letters/${id}`);
        setLetterData(response.data);
      } catch (error) {
        toast.error("Failed to load cover letter");
        navigate("/resumes");
      } finally {
        setLoading(false);
      }
    };

    if (letterId) {
      loadLetter(letterId);
    }
  }, [letterId, navigate]);

  // Pre-fill job info from query params (when coming from job listing)
  useEffect(() => {
    const jobTitle = searchParams.get("jobTitle");
    const company = searchParams.get("company");
    
    if (jobTitle || company) {
      setLetterData(prev => ({
        ...prev,
        title: `Cover Letter - ${company || jobTitle}`,
        content: {
          ...prev.content,
          jobTitle: jobTitle || prev.content.jobTitle,
          companyName: company || prev.content.companyName,
        }
      }));
    }
  }, [searchParams]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (letterId) {
        await axiosInstance.put(`/auth/cover-letters/${letterId}`, letterData);
        toast.success("Cover letter updated!");
      } else {
        const response = await axiosInstance.post("/auth/cover-letters", letterData);
        toast.success("Cover letter saved!");
        navigate(`/cover-letter-builder/${response.data.id}`, { replace: true });
      }
    } catch (error) {
      toast.error("Failed to save cover letter");
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axiosInstance.post(
        `/auth/cover-letters/${letterId}/export`,
        {},
        { responseType: "blob" }
      );
      
      const blob = new Blob([response.data], { type: "text/html" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${letterData.title.replace(/[^a-z0-9]/gi, "_")}.html`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Cover letter exported!");
    } catch (error) {
      toast.error("Failed to export cover letter");
    }
  };

  const updateContent = (field: keyof CoverLetterData["content"], value: string) => {
    setLetterData(prev => ({
      ...prev,
      content: { ...prev.content, [field]: value }
    }));
  };

  const autoFillFromProfile = () => {
    if (userProfile) {
      setLetterData(prev => ({
        ...prev,
        content: {
          ...prev.content,
          senderName: userProfile.fullName || prev.content.senderName,
          senderEmail: userProfile.email || prev.content.senderEmail,
          senderPhone: userProfile.phone || prev.content.senderPhone,
        }
      }));
      toast.success("Info filled from your profile!");
    }
  };

  const handleTailor = async () => {
    if (!letterId) { toast.error("Save your cover letter first before tailoring."); return; }
    if (!tailorJobDesc.trim()) { toast.error("Paste a job description to tailor against."); return; }
    setTailoring(true);
    try {
      const res = await axiosInstance.post(`/auth/cover-letters/${letterId}/tailor`, {
        jobDescription: tailorJobDesc,
        jobTitle: letterData.content.jobTitle,
        companyName: letterData.content.companyName,
      });
      const t = res.data?.tailored;
      if (!t) { toast.error("AI tailoring returned no result. Try again."); return; }
      setLetterData(prev => ({
        ...prev,
        content: {
          ...prev.content,
          ...(t.openingParagraph ? { openingParagraph: t.openingParagraph } : {}),
          ...(t.bodyParagraph1   ? { bodyParagraph1:   t.bodyParagraph1   } : {}),
          ...(t.bodyParagraph2   ? { bodyParagraph2:   t.bodyParagraph2   } : {}),
          ...(t.closingParagraph ? { closingParagraph: t.closingParagraph } : {}),
        },
      }));
      toast.success("Cover letter tailored by AI!");
      setShowTailorModal(false);
      setTailorJobDesc("");
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to tailor cover letter");
    } finally {
      setTailoring(false);
    }
  };

  const renderRecipientSection = () => (
    <div className="clb-section-content">
      <h3>Recipient Information</h3>
      <p className="clb-section-hint">Enter details about who will receive this letter</p>
      
      <div className="clb-form-grid">
        <div className="clb-form-group">
          <label>Recipient Name</label>
          <input
            type="text"
            value={letterData.content.recipientName}
            onChange={e => updateContent("recipientName", e.target.value)}
            placeholder="e.g., John Smith"
          />
        </div>
        <div className="clb-form-group">
          <label>Recipient Title</label>
          <input
            type="text"
            value={letterData.content.recipientTitle}
            onChange={e => updateContent("recipientTitle", e.target.value)}
            placeholder="e.g., HR Manager"
          />
        </div>
      </div>

      <div className="clb-form-group">
        <label>Company Name *</label>
        <input
          type="text"
          value={letterData.content.companyName}
          onChange={e => updateContent("companyName", e.target.value)}
          placeholder="e.g., Acme Corporation"
          required
        />
      </div>

      <div className="clb-form-group">
        <label>Company Address</label>
        <textarea
          value={letterData.content.companyAddress}
          onChange={e => updateContent("companyAddress", e.target.value)}
          placeholder="123 Business Ave, Suite 100&#10;City, State 12345"
          rows={2}
        />
      </div>

      <div className="clb-form-group">
        <label>Position Applied For *</label>
        <input
          type="text"
          value={letterData.content.jobTitle}
          onChange={e => updateContent("jobTitle", e.target.value)}
          placeholder="e.g., Software Engineer"
          required
        />
      </div>

      <div className="clb-form-group">
        <label>Salutation</label>
        <input
          type="text"
          value={letterData.content.salutation}
          onChange={e => updateContent("salutation", e.target.value)}
          placeholder="Dear Hiring Manager,"
        />
        <span className="clb-field-hint">Use "Dear Hiring Manager," if you don't know the name</span>
      </div>
    </div>
  );

  const renderIntroSection = () => (
    <div className="clb-section-content">
      <h3>Introduction</h3>
      <p className="clb-section-hint">Start strong by stating the position and your enthusiasm</p>
      
      <div className="clb-form-group">
        <label>Opening Paragraph *</label>
        <textarea
          value={letterData.content.openingParagraph}
          onChange={e => updateContent("openingParagraph", e.target.value)}
          placeholder={`I am writing to express my strong interest in the ${letterData.content.jobTitle || "[Position]"} position at ${letterData.content.companyName || "[Company]"}. With my background in [your field/skill], I am confident that I would be a valuable addition to your team.`}
          rows={5}
          required
        />
        <span className="clb-field-hint">
          Tip: Mention the position, how you found it, and why you're excited about it
        </span>
      </div>
    </div>
  );

  const renderBodySection = () => (
    <div className="clb-section-content">
      <h3>Body Paragraphs</h3>
      <p className="clb-section-hint">Highlight your relevant skills and experiences</p>
      
      <div className="clb-form-group">
        <label>First Body Paragraph *</label>
        <textarea
          value={letterData.content.bodyParagraph1}
          onChange={e => updateContent("bodyParagraph1", e.target.value)}
          placeholder="In my current/previous role at [Company], I have developed strong skills in [relevant skills]. I successfully [specific achievement with measurable result]. This experience has prepared me to contribute effectively to your team."
          rows={5}
          required
        />
        <span className="clb-field-hint">
          Tip: Focus on your most relevant experience and quantifiable achievements
        </span>
      </div>

      <div className="clb-form-group">
        <label>Second Body Paragraph (Optional)</label>
        <textarea
          value={letterData.content.bodyParagraph2}
          onChange={e => updateContent("bodyParagraph2", e.target.value)}
          placeholder="Additionally, I am particularly drawn to [Company] because of [specific reason: company culture, mission, recent project, etc.]. My skills in [additional skills] align well with your team's focus on [company focus area]."
          rows={5}
        />
        <span className="clb-field-hint">
          Tip: Show you've researched the company and explain why you're a good fit
        </span>
      </div>
    </div>
  );

  const renderClosingSection = () => (
    <div className="clb-section-content">
      <h3>Closing</h3>
      <p className="clb-section-hint">End with a call to action and professional sign-off</p>
      
      <div className="clb-form-group">
        <label>Closing Paragraph *</label>
        <textarea
          value={letterData.content.closingParagraph}
          onChange={e => updateContent("closingParagraph", e.target.value)}
          placeholder="I am enthusiastic about the opportunity to bring my skills and experience to [Company]. I would welcome the chance to discuss how I can contribute to your team's success. Thank you for considering my application. I look forward to the opportunity to speak with you."
          rows={4}
          required
        />
        <span className="clb-field-hint">
          Tip: Express enthusiasm, thank them, and indicate your desire for an interview
        </span>
      </div>

      <div className="clb-form-group">
        <label>Sign Off</label>
        <select
          value={letterData.content.signOff}
          onChange={e => updateContent("signOff", e.target.value)}
        >
          <option value="Sincerely,">Sincerely,</option>
          <option value="Best regards,">Best regards,</option>
          <option value="Kind regards,">Kind regards,</option>
          <option value="Respectfully,">Respectfully,</option>
          <option value="Thank you,">Thank you,</option>
        </select>
      </div>
    </div>
  );

  const renderSenderSection = () => (
    <div className="clb-section-content">
      <h3>Your Contact Information</h3>
      <p className="clb-section-hint">Enter your contact details for the letter header</p>
      
      {userProfile && (
        <button type="button" className="clb-autofill-btn" onClick={autoFillFromProfile}>
          ✨ Auto-fill from Profile
        </button>
      )}

      <div className="clb-form-grid">
        <div className="clb-form-group">
          <label>Your Full Name *</label>
          <input
            type="text"
            value={letterData.content.senderName}
            onChange={e => updateContent("senderName", e.target.value)}
            placeholder="Your full name"
            required
          />
        </div>
        <div className="clb-form-group">
          <label>Date</label>
          <input
            type="date"
            value={letterData.content.date}
            onChange={e => updateContent("date", e.target.value)}
          />
        </div>
      </div>

      <div className="clb-form-grid">
        <div className="clb-form-group">
          <label>Email *</label>
          <input
            type="email"
            value={letterData.content.senderEmail}
            onChange={e => updateContent("senderEmail", e.target.value)}
            placeholder="your.email@example.com"
            required
          />
        </div>
        <div className="clb-form-group">
          <label>Phone</label>
          <input
            type="tel"
            value={letterData.content.senderPhone}
            onChange={e => updateContent("senderPhone", e.target.value)}
            placeholder="+1 (555) 123-4567"
          />
        </div>
      </div>

      <div className="clb-form-group">
        <label>Your Address</label>
        <textarea
          value={letterData.content.senderAddress}
          onChange={e => updateContent("senderAddress", e.target.value)}
          placeholder="123 Your Street&#10;City, State 12345"
          rows={2}
        />
      </div>
    </div>
  );

  const renderSectionContent = () => {
    switch (activeSection) {
      case "recipient": return renderRecipientSection();
      case "intro": return renderIntroSection();
      case "body": return renderBodySection();
      case "closing": return renderClosingSection();
      case "sender": return renderSenderSection();
      default: return null;
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { 
      year: "numeric", 
      month: "long", 
      day: "numeric" 
    });
  };

  const renderPreview = () => {
    const { content, template } = letterData;
    
    return (
      <div className={`clb-preview clb-template-${template}`}>
        <div className="clb-preview-header">
          <div className="clb-sender-info">
            <strong>{content.senderName || "Your Name"}</strong>
            {content.senderAddress && <p>{content.senderAddress}</p>}
            <p>
              {content.senderEmail}
              {content.senderPhone && ` | ${content.senderPhone}`}
            </p>
          </div>
          <div className="clb-date">{formatDate(content.date)}</div>
        </div>

        {(content.recipientName || content.companyName) && (
          <div className="clb-recipient-info">
            {content.recipientName && <p>{content.recipientName}</p>}
            {content.recipientTitle && <p>{content.recipientTitle}</p>}
            {content.companyName && <p>{content.companyName}</p>}
            {content.companyAddress && <p>{content.companyAddress}</p>}
          </div>
        )}

        <div className="clb-salutation">{content.salutation || "Dear Hiring Manager,"}</div>

        <div className="clb-body">
          {content.openingParagraph && <p>{content.openingParagraph}</p>}
          {content.bodyParagraph1 && <p>{content.bodyParagraph1}</p>}
          {content.bodyParagraph2 && <p>{content.bodyParagraph2}</p>}
          {content.closingParagraph && <p>{content.closingParagraph}</p>}
        </div>

        <div className="clb-signature">
          <p className="clb-signoff">{content.signOff}</p>
          <p className="clb-sender-name">{content.senderName || "Your Name"}</p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="clb-container">
        <div className="clb-loading">Loading cover letter...</div>
      </div>
    );
  }

  return (
    <div className="clb-container">
      <div className="clb-header">
        <div className="clb-header-left">
          <button className="clb-back-btn" onClick={() => navigate("/resumes")}>
            ← Back to Documents
          </button>
          <input
            type="text"
            className="clb-title-input"
            value={letterData.title}
            onChange={e => setLetterData(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Cover Letter Title"
          />
        </div>
        <div className="clb-header-right">
          <select
            className="clb-template-select"
            value={letterData.template}
            onChange={e => setLetterData(prev => ({ 
              ...prev, 
              template: e.target.value as CoverLetterData["template"] 
            }))}
          >
            {TEMPLATES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <button 
            className="clb-preview-btn"
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? "✏️ Edit" : "👁️ Preview"}
          </button>
          <button 
            className="clb-save-btn" 
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "💾 Save"}
          </button>
          {letterId && (
            <>
              <button className="clb-export-btn" onClick={handleExport}>
                📄 Export
              </button>
              <button className="clb-tailor-btn" onClick={() => setShowTailorModal(true)}>
                ✨ Tailor with AI
              </button>
            </>
          )}
        </div>
      </div>

      {/* AI Tailor Modal */}
      {showTailorModal && (
        <div className="clb-modal-overlay" onClick={() => setShowTailorModal(false)}>
          <div className="clb-modal" onClick={e => e.stopPropagation()}>
            <h3>✨ Tailor Cover Letter with AI</h3>
            <p className="clb-modal-hint">
              Paste the job description below. The AI will rewrite your opening, body, and closing paragraphs to match the role.
            </p>
            <textarea
              className="clb-tailor-textarea"
              rows={10}
              placeholder="Paste the full job description here…"
              value={tailorJobDesc}
              onChange={e => setTailorJobDesc(e.target.value)}
            />
            <div className="clb-modal-actions">
              <button
                className="clb-save-btn"
                onClick={handleTailor}
                disabled={tailoring || !tailorJobDesc.trim()}
              >
                {tailoring ? "Tailoring…" : "✨ Apply AI Tailoring"}
              </button>
              <button className="clb-cancel-btn" onClick={() => setShowTailorModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="clb-main">
        {!showPreview ? (
          <>
            <nav className="clb-sidebar">
              {SECTIONS.map(section => (
                <button
                  key={section.key}
                  className={`clb-nav-item ${activeSection === section.key ? "active" : ""}`}
                  onClick={() => setActiveSection(section.key)}
                >
                  <span className="clb-nav-icon">{section.icon}</span>
                  <span className="clb-nav-label">{section.label}</span>
                </button>
              ))}
            </nav>

            <div className="clb-editor">
              {renderSectionContent()}
              
              <div className="clb-navigation">
                {activeSection !== "recipient" && (
                  <button 
                    className="clb-nav-prev"
                    onClick={() => {
                      const idx = SECTIONS.findIndex(s => s.key === activeSection);
                      if (idx > 0) setActiveSection(SECTIONS[idx - 1].key);
                    }}
                  >
                    ← Previous
                  </button>
                )}
                {activeSection !== "sender" && (
                  <button 
                    className="clb-nav-next"
                    onClick={() => {
                      const idx = SECTIONS.findIndex(s => s.key === activeSection);
                      if (idx < SECTIONS.length - 1) setActiveSection(SECTIONS[idx + 1].key);
                    }}
                  >
                    Next →
                  </button>
                )}
              </div>
            </div>

            <div className="clb-mini-preview">
              <h4>Live Preview</h4>
              {renderPreview()}
            </div>
          </>
        ) : (
          <div className="clb-full-preview">
            {renderPreview()}
          </div>
        )}
      </div>
    </div>
  );
};

export default CoverLetterBuilder;
